import { DocumentStatus } from '@synergy/models';
import { createSelector } from 'reselect';
import dayjs from 'dayjs';

import { AppState } from '../../../../../store/models/app-state';
import { OverviewFormValues } from '../../models/overview-form-values';
import { StepData } from '../../../../../common/components/new-stepper/step-data';
import { defaultCurrencySelector } from '../../../../../common/selectors/currency-selectors';
import { userSelector } from '../../../../../common/selectors/user-selectors';

const invoiceDetailOverviewStepDataState = (state: AppState) => state.invoiceDetail.overview.stepData;
const invoiceDetailOverviewState = (state: AppState) => state.invoiceDetail.overview;

/**
 * The defaults the mask fills in for values the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2137): compared against raw server
 * values, every default would read as something the user typed.
 */
const withDefaults = (
    values: OverviewFormValues,
    currency: string,
    user: ReturnType<typeof userSelector>
): OverviewFormValues => ({
    id: values.id,
    status: values.status ?? DocumentStatus.Created,

    isCredit: values.isCredit ?? false,
    shouldSendEmail: values.shouldSendEmail ?? true,
    currency: values.currency ?? currency,
    poolBankInfo: values.poolBankInfo ?? undefined,
    responsibleContact: values.responsibleContact ?? user.contact,
    // The start of the day, not the moment: this runs once for the form and
    // once for the unsaved-changes baseline, and two calls to `new Date()` are
    // milliseconds apart — which made every draft report unsaved input forever
    // (#2210). An invoice date carries no time of day anyway.
    invoiceDate: values.invoiceDate ?? dayjs().startOf('day').toDate(),

    recipientAccount: values.recipientAccount ?? null,
    recipientContact: values.recipientContact ?? null,
    invoiceAddress: values.invoiceAddress ?? null,
    recipientOrderContact: values.recipientOrderContact ?? null,

    functionalAsset: values.functionalAsset ?? null,
    costCentre: values.costCentre ?? '',
    orderReference: values.orderReference ?? '',
    orderDate: values.orderDate,

    supplierAccount: values.supplierAccount ?? null,
    supplierContact: values.supplierContact ?? null,
    deliveryNumber: values.deliveryNumber ?? '',
    deliveryDate: values.deliveryDate,
    incomingInvoiceNumber: values.incomingInvoiceNumber ?? '',

    paymentTerm: values.paymentTerm ?? null,

    noteToRecipient: values.noteToRecipient ?? '',
    internalNote: values.internalNote ?? '',

    number: values.number ?? '',
    classifications: values.classifications ?? [],
});

export const invoiceDetailOverviewDataSelector = createSelector(
    invoiceDetailOverviewStepDataState,
    defaultCurrencySelector,
    userSelector,
    (stepData, currency, user): StepData<OverviewFormValues> => ({
        ...stepData,
        values: withDefaults(stepData.values, currency, user),
    })
);

/** What the mask compares against to decide whether anything is unsaved. */
export const invoiceDetailOverviewBaselineSelector = createSelector(
    (state: AppState) => state.invoiceDetail.overview.baseline,
    defaultCurrencySelector,
    userSelector,
    (baseline, currency, user): OverviewFormValues | undefined =>
        baseline ? withDefaults(baseline, currency, user) : undefined
);

export const isCreditSelector = createSelector(invoiceDetailOverviewState, state => {
    return state.isCredit;
});
